import React from 'react';

import {
  EmployeeNotesDocument,
  EmployeeNotesQuery,
  EmployeeNotesQueryVariables,
  useCreateEmployeeNoteMutation,
  useEmployeeNotesQuery,
} from 'src/api';

import { useNotification } from '../app';

type EmployeeNotesHookOptions = {
  variables?: EmployeeNotesQueryVariables;
  skip?: boolean;
};

export const useEmployeeNotes = ({ variables, skip }: EmployeeNotesHookOptions) => {
  const { createApolloContext } = useNotification();

  // No pagination: an employee has a handful of notes, and the server caps an unbounded list at
  // 1000 rows. Bring back take/skip if the notes section ever needs to page.
  const { data, loading } = useEmployeeNotesQuery({ variables, skip });

  const [createEmployeeNoteMutation] = useCreateEmployeeNoteMutation({
    context: createApolloContext(),
  });

  const employeeNotes = React.useMemo(() => data?.employeeNoteList || [], [data?.employeeNoteList]);

  const createEmployeeNote = React.useCallback(
    async (employeeSsnEncrypted: string, comment: string) => {
      const result = await createEmployeeNoteMutation({
        variables: {
          employeeSsnEncrypted,
          comment,
        },
        update: (cache, { data }) => {
          if (!data?.createEmployeeNote) return;
          cache.updateQuery<EmployeeNotesQuery, EmployeeNotesQueryVariables>(
            { query: EmployeeNotesDocument, variables },
            queryData =>
              queryData && {
                employeeNoteList: [data.createEmployeeNote, ...queryData.employeeNoteList],
              },
          );
        },
      });

      return result.data?.createEmployeeNote;
    },
    [createEmployeeNoteMutation, variables],
  );

  return {
    employeeNotes,
    loading,
    createEmployeeNote,
  };
};
